import { Invoice } from "@/features/invoices/types/invoices";
import { FounderLevel, LevelProgress, Milestone } from "@/types/levels";
import { CurrentUser } from "@/types/user";

interface EarningsData {
  ytd: number;
}

export const calculateUniqueClientsCount = (invoices: Invoice[]): number => {
  const uniqueCustomers = new Set();
  invoices.forEach((invoice) => {
    if (invoice.customer) {
      uniqueCustomers.add(invoice.customer);
    }
  });
  return uniqueCustomers.size;
};

export const calculateCurrentLevel = (
  user: CurrentUser,
  earnings: EarningsData | undefined,
  invoices: Invoice[],
): FounderLevel | null => {
  const milestones = calculateMilestones(user, earnings, invoices);

  // Check Stack X requirements
  const stackXMilestones = milestones.filter((m) =>
    [
      "define-niche",
      "demo-approved",
      "playbook-completed",
      "billing-100k",
      "testimonials-5",
    ].includes(m.id),
  );

  if (stackXMilestones.every((m) => m.completed)) {
    return FounderLevel.STACK_X;
  }

  // Check Stack Pro requirements
  const stackProMilestones = milestones.filter((m) =>
    [
      "define-niche",
      "demo-approved",
      "playbook-completed",
      "billing-10k",
      "testimonials-2",
    ].includes(m.id),
  );

  if (stackProMilestones.every((m) => m.completed)) {
    return FounderLevel.STACK_PRO;
  }

  // Check Stack Certified requirements
  const stackCertifiedMilestones = milestones.filter((m) =>
    ["define-niche", "demo-approved", "playbook-completed"].includes(m.id),
  );

  if (stackCertifiedMilestones.every((m) => m.completed)) {
    return FounderLevel.STACK_CERTIFIED;
  }

  return null;
};

export const calculateMilestones = (
  user: CurrentUser,
  earnings: EarningsData | undefined,
  invoices: Invoice[],
): Milestone[] => {
  const uniqueClientsCount = calculateUniqueClientsCount(invoices);
  const ytdEarnings = earnings?.ytd || 0;
  const testimonialsCount = user.testimonialsCount || 0;

  return [
    {
      id: "define-niche",
      title: "Define your Niche",
      completed: !!(
        user.nicheWho &&
        user.nicheWho.trim().length > 0 &&
        user.nicheWhat &&
        user.nicheWhat.trim().length > 0 &&
        user.businessCategory &&
        user.businessCategory.length > 0 &&
        user.expertise &&
        user.expertise.trim().length > 0
      ),
      isAutomatic: true,
    },
    {
      id: "demo-approved",
      title: "Share a demo with the community",
      completed: user.hasSharedDemo || false,
      isAutomatic: false,
    },
    {
      id: "playbook-completed",
      title: "Complete the Playbook",
      completed: user.hasCompletedPlaybook || false,
      isAutomatic: false,
    },
    {
      id: "billing-10k",
      title: "Billing $10k through Stack",
      completed: ytdEarnings >= 10000,
      progress: Math.min(ytdEarnings, 10000),
      total: 10000,
      isAutomatic: true,
    },
    {
      id: "testimonials-2",
      title: "Have 2x testimonials on your Stack Landing Page",
      completed: testimonialsCount >= 2,
      progress: Math.min(testimonialsCount, 2),
      total: 2,
      isAutomatic: false,
    },
    {
      id: "billing-100k",
      title: "Billing $100k through Stack",
      completed: ytdEarnings >= 100000,
      progress: Math.min(ytdEarnings, 100000),
      total: 100000,
      isAutomatic: true,
    },
    {
      id: "testimonials-5",
      title: "Have 5x testimonials on your Stack Page",
      completed: testimonialsCount >= 5,
      progress: Math.min(testimonialsCount, 5),
      total: 5,
      isAutomatic: false,
    },
    {
      id: "unique-clients-5",
      title: "Work with 5 unique clients",
      completed: uniqueClientsCount >= 5,
      progress: Math.min(uniqueClientsCount, 5),
      total: 5,
      isAutomatic: true,
    },
  ];
};

export const getNextLevel = (
  currentLevel: FounderLevel | null,
): FounderLevel | null => {
  if (!currentLevel) return FounderLevel.STACK_CERTIFIED;
  if (currentLevel === FounderLevel.STACK_CERTIFIED)
    return FounderLevel.STACK_PRO;
  if (currentLevel === FounderLevel.STACK_PRO) return FounderLevel.STACK_X;
  return null;
};

export const getLevelProgress = (
  user: CurrentUser,
  earnings: EarningsData | undefined,
  invoices: Invoice[],
): LevelProgress => {
  const currentLevel = calculateCurrentLevel(user, earnings, invoices);
  const nextLevel = getNextLevel(currentLevel);
  const milestones = calculateMilestones(user, earnings, invoices);

  return {
    currentLevel,
    nextLevel,
    milestones,
    levelUnlocked: currentLevel !== null,
  };
};
